// features/customer/components/GuestAccessManager.tsx
import React, { useState, useEffect } from 'react';
import { API_V1_BASE_URL } from '../../config/api';
import { formatAuditAction } from '../../config/auditActions';

interface GuestUser {
    id: string;
    assetId: string;
    assetName: string;
    assetType: 'campaign' | 'document' | 'report' | 'dashboard';
    accessToken: string;
    accessUrl: string;
    createdAt: string;
    expiresAt: string;
    accessCount: number;
    isActive: boolean;
    createdBy: string;
    guestEmail: string;
    guestName: string;
    guestCompany: string;
}

interface GuestAccessLog {
    id: string;
    linkId: string;
    accessedAt: string;
    ipAddress: string;
    userAgent: string;
    assetAccessed: string;
    guestEmail: string;
    guestName: string;
    authenticationMethod: 'none' | 'email_verified';
    action: 'login' | 'logout' | 'view_access';
    location?: string;
    sessionId?: string;
}

interface AuditTenant {
    id: string;
    name: string;
}

interface SavedContact {
    id: string;
    email: string;
    name: string;
    company: string;
    lastUsed: string;
    usageCount: number;
    isFavorite: boolean;
    recentAssets?: string[];
}

interface GuestAssignment {
    guestId: string;
    assetIds: string[];
    assignedAt: string;
}

type UserSelectionType = 'new' | 'existing' | null;

interface GuestAccessManagerProps {
    tenantId: string | number;
}

const GuestAccessManager: React.FC<GuestAccessManagerProps> = ({ tenantId }) => {
    const [guestUsers, setGuestUsers] = useState<GuestUser[]>([]);
    const [accessLogs, setAccessLogs] = useState<GuestAccessLog[]>([]);
    const [auditTenants, setAuditTenants] = useState<AuditTenant[]>([]);
    const [selectedAuditTenantId, setSelectedAuditTenantId] = useState('');
    const [selectedAuditAction, setSelectedAuditAction] = useState('');
    const [availableAuditActions, setAvailableAuditActions] = useState<string[]>([]);
    const [accessLogsLoading, setAccessLogsLoading] = useState(false);
    const [accessLogsError, setAccessLogsError] = useState('');
    const [savedContacts, setSavedContacts] = useState<SavedContact[]>([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState<{ id: string; name: string; type: string }[]>([]);
    const [expiryDays, setExpiryDays] = useState(7);
    const [showAuditLogs, setShowAuditLogs] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [showBulkActions, setShowBulkActions] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'expired' | 'deactivated'>('all');
    const [filterAsset, setFilterAsset] = useState<string>('all');
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportDateRange, setExportDateRange] = useState({ start: '', end: '' });
    const [showSavedContacts, setShowSavedContacts] = useState(false);
    const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
    const [isMultiAssetMode, setIsMultiAssetMode] = useState(false);
    const [assignments, setAssignments] = useState<GuestAssignment[]>([]);

    // New user creation flow state
    const [userSelectionType, setUserSelectionType] = useState<UserSelectionType>(null);
    const [selectedExistingUser, setSelectedExistingUser] = useState<SavedContact | null>(null);
    const [saveForFuture, setSaveForFuture] = useState(true);
    const [showSavePrompt, setShowSavePrompt] = useState(false);
    const [tempCreatedGuest, setTempCreatedGuest] = useState<{ email: string; name: string; company: string } | null>(null);

    // User audit log modal state
    const [showUserAuditModal, setShowUserAuditModal] = useState(false);
    const [selectedUserForAudit, setSelectedUserForAudit] = useState<GuestUser | null>(null);
    const [userAuditLogs, setUserAuditLogs] = useState<GuestAccessLog[]>([]);
    const [userExportDateRange, setUserExportDateRange] = useState({ start: '', end: '' });

    // Guest information form
    const [guestInfo, setGuestInfo] = useState({
        email: '',
        name: '',
        company: ''
    });
    const [formErrors, setFormErrors] = useState({
        email: '',
        name: '',
        company: ''
    });

    // Available campaigns/assets
    const availableAssets = [
        { id: 'camp-1', name: 'Summer Sale Campaign 2024', type: 'campaign' },
        { id: 'camp-2', name: 'Product Launch Campaign - Q3', type: 'campaign' },
        { id: 'camp-3', name: 'Email Marketing Campaign', type: 'campaign' },
        { id: 'camp-4', name: 'Social Media Promo Campaign', type: 'campaign' },
        { id: 'camp-5', name: 'Affiliate Marketing Campaign', type: 'campaign' },
        { id: 'camp-6', name: 'Holiday Special Campaign', type: 'campaign' },
        { id: 'doc-1', name: 'Q1 Financial Report.pdf', type: 'document' },
        { id: 'doc-2', name: 'Annual Budget.xlsx', type: 'document' },
        { id: 'dash-1', name: 'Sales Performance Dashboard', type: 'dashboard' },
        { id: 'dash-2', name: 'Customer Analytics Dashboard', type: 'dashboard' },
        { id: 'report-1', name: 'User Engagement Report', type: 'report' },
        { id: 'report-2', name: 'ROI Analysis Report', type: 'report' }
    ];

    useEffect(() => {
        loadGuestUsers();
        loadAuditTenants();
        loadAuditActions();
        loadAccessLogs();
        loadSavedContacts();
        loadAssignments();
    }, [tenantId]);

    const loadGuestUsers = () => {
        // Mock data - in production, fetch from API
        const mockUsers: GuestUser[] = [
            {
                id: '1',
                assetId: 'camp-1',
                assetName: 'Summer Sale Campaign 2024',
                assetType: 'campaign',
                accessToken: 'abc123xyz',
                accessUrl: 'https://app.simb.com/guest/view/abc123xyz',
                createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
                expiresAt: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
                accessCount: 3,
                isActive: true,
                createdBy: 'admin@tenanta.com',
                guestEmail: 'john.doe@external.com',
                guestName: 'John Doe',
                guestCompany: 'Partner Corp'
            },
            {
                id: '2',
                assetId: 'camp-3',
                assetName: 'Email Marketing Campaign',
                assetType: 'campaign',
                accessToken: 'def456uvw',
                accessUrl: 'https://app.simb.com/guest/view/def456uvw',
                createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
                expiresAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
                accessCount: 8,
                isActive: true,
                createdBy: 'admin@tenanta.com',
                guestEmail: 'jane.smith@partner.com',
                guestName: 'Jane Smith',
                guestCompany: 'Partner Solutions'
            },
            {
                id: '3',
                assetId: 'camp-2',
                assetName: 'Product Launch Campaign - Q3',
                assetType: 'campaign',
                accessToken: 'ghi789jkl',
                accessUrl: 'https://app.simb.com/guest/view/ghi789jkl',
                createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
                expiresAt: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
                accessCount: 12,
                isActive: false,
                createdBy: 'admin@tenanta.com',
                guestEmail: 'mike.wilson@external.com',
                guestName: 'Mike Wilson',
                guestCompany: 'Digital Agency'
            }
        ];
        setGuestUsers(mockUsers);
    };

    const loadAuditTenants = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_V1_BASE_URL}/audit-logs/tenants`, {
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
            const result = await response.json().catch(() => null);
            if (response.ok && result?.success) {
                setAuditTenants(result.data || []);
            }
        } catch {
            setAuditTenants([]);
        }
    };

    const loadAccessLogs = async (auditTenantId = selectedAuditTenantId, action = selectedAuditAction) => {
        setAccessLogsLoading(true);
        setAccessLogsError('');

        try {
            const token = localStorage.getItem('accessToken');
            const params = new URLSearchParams({ limit: '100' });
            if (auditTenantId) params.set('tenantId', auditTenantId);
            if (action) params.set('action', action);
            const response = await fetch(`${API_V1_BASE_URL}/audit-logs?${params.toString()}`, {
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
            const result = await response.json().catch(() => null);

            if (!response.ok || !result?.success) {
                throw new Error(result?.message || 'Failed to load audit logs');
            }

            const logs = result.data?.data || [];
            setAccessLogs(logs.map((log: any) => {
                const action = String(log.action || '').toLowerCase();
                return {
                    id: log.id,
                    linkId: log.request_id || log.id,
                    accessedAt: log.timestamp,
                    ipAddress: log.ip_address || 'N/A',
                    userAgent: log.user_agent || 'N/A',
                    assetAccessed: log.details || log.action,
                    guestEmail: log.user_email || 'N/A',
                    guestName: log.user?.firstName || log.user_email || 'N/A',
                    authenticationMethod: 'email_verified',
                    action: action === 'logout' ? 'logout' : action === 'login' ? 'login' : 'view_access',
                    location: [log.location_city, log.location_country].filter(Boolean).join(', '),
                    sessionId: log.request_id || undefined,
                };
            }));
        } catch (error) {
            setAccessLogs([]);
            setAccessLogsError(error instanceof Error ? error.message : 'Failed to load audit logs');
        } finally {
            setAccessLogsLoading(false);
        }
    };

    const loadAuditActions = async (auditTenantId = selectedAuditTenantId) => {
        try {
            const token = localStorage.getItem('accessToken');
            const params = new URLSearchParams();
            if (auditTenantId) params.set('tenantId', auditTenantId);
            const response = await fetch(`${API_V1_BASE_URL}/audit-logs/actions?${params.toString()}`, {
                headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            });
            const result = await response.json().catch(() => null);
            setAvailableAuditActions(response.ok && result?.success ? result.data || [] : []);
        } catch {
            setAvailableAuditActions([]);
        }
    };

    const loadSavedContacts = () => {
        // Mock saved contacts
        const mockContacts: SavedContact[] = [
            {
                id: 'contact_1',
                email: 'john.doe@external.com',
                name: 'John Doe',
                company: 'Partner Corp',
                lastUsed: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
                usageCount: 3,
                isFavorite: true,
                recentAssets: ['camp-1', 'camp-2']
            },
            {
                id: 'contact_2',
                email: 'jane.smith@partner.com',
                name: 'Jane Smith',
                company: 'Partner Solutions',
                lastUsed: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
                usageCount: 2,
                isFavorite: false,
                recentAssets: ['camp-3']
            },
            {
                id: 'contact_3',
                email: 'mike.wilson@external.com',
                name: 'Mike Wilson',
                company: 'Digital Agency',
                lastUsed: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
                usageCount: 5,
                isFavorite: true,
                recentAssets: ['camp-2', 'report-1']
            },
            {
                id: 'contact_4',
                email: 'sarah.connor@tech.com',
                name: 'Sarah Connor',
                company: 'Tech Innovations',
                lastUsed: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
                usageCount: 1,
                isFavorite: false
            },
            {
                id: 'contact_5',
                email: 'robert.downey@agency.com',
                name: 'Robert Downey',
                company: 'Creative Agency',
                lastUsed: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
                usageCount: 1,
                isFavorite: true
            }
        ];
        setSavedContacts(mockContacts);
    };

    const loadAssignments = () => {
        // Mock guest assignments
        const mockAssignments: GuestAssignment[] = [
            {
                guestId: '1',
                assetIds: ['camp-1', 'camp-3'],
                assignedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
            }
        ];
        setAssignments(mockAssignments);
    };

    const viewUserAuditLogs = (user: GuestUser) => {
        setSelectedUserForAudit(user);
        const userLogs = accessLogs.filter(log => log.guestEmail === user.guestEmail);
        setUserAuditLogs(userLogs);
        setShowUserAuditModal(true);
    };

    const exportUserAuditLogs = () => {
        if (!selectedUserForAudit) return;

        let logsToExport = userAuditLogs;

        if (userExportDateRange.start && userExportDateRange.end) {
            logsToExport = userAuditLogs.filter(log =>
                log.accessedAt >= userExportDateRange.start &&
                log.accessedAt <= userExportDateRange.end
            );
        }

        const csvData = logsToExport.map(log => ({
            'Date/Time': new Date(log.accessedAt).toLocaleString(),
            'Guest Name': log.guestName,
            'Guest Email': log.guestEmail,
            'Action': log.action,
            'Asset': log.assetAccessed,
            'IP Address': log.ipAddress,
            'Location': log.location || 'N/A',
            'User Agent': log.userAgent,
            'Session ID': log.sessionId || 'N/A'
        }));

        const csvContent = [
            Object.keys(csvData[0] || {}).join(','),
            ...csvData.map(row => Object.values(row).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_logs_${selectedUserForAudit.guestName}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        alert(`✓ Audit logs exported for ${selectedUserForAudit.guestName}!`);
    };

    const saveContact = (email: string, name: string, company: string, assetIds: string[]) => {
        const existingContact = savedContacts.find(c => c.email === email);

        if (existingContact) {
            // Update existing contact
            setSavedContacts(savedContacts.map(contact =>
                contact.email === email
                    ? {
                        ...contact,
                        lastUsed: new Date().toISOString(),
                        usageCount: contact.usageCount + 1,
                        recentAssets: [...new Set([...(contact.recentAssets || []), ...assetIds])]
                    }
                    : contact
            ));
        } else {
            // Add new contact
            const newContact: SavedContact = {
                id: `contact_${Date.now()}`,
                email,
                name,
                company,
                lastUsed: new Date().toISOString(),
                usageCount: 1,
                isFavorite: false,
                recentAssets: assetIds
            };
            setSavedContacts([newContact, ...savedContacts]);
        }
    };

    const loadContactDetails = (contact: SavedContact) => {
        setSelectedExistingUser(contact);
        setGuestInfo({
            email: contact.email,
            name: contact.name,
            company: contact.company
        });
        setSelectedContactId(contact.id);

        // Suggest recent assets
        if (contact.recentAssets && contact.recentAssets.length > 0) {
            const recentAssetsList = availableAssets.filter(asset =>
                contact.recentAssets?.includes(asset.id)
            );
            if (recentAssetsList.length > 0 && window.confirm(`Would you like to pre-select recently used assets for ${contact.name}?`)) {
                setSelectedAsset(recentAssetsList);
            }
        }
    };

    const reactivateExpiredGuest = (user: GuestUser) => {
        if (window.confirm(`Reactivate access for ${user.guestName}? This will extend their access by ${expiryDays} days.`)) {
            const newExpiryDate = new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000);
            const newAccessToken = generateAccessToken();
            const newAccessUrl = `${window.location.origin}/guest/access/${newAccessToken}`;

            setGuestUsers(guestUsers.map(u =>
                u.id === user.id
                    ? {
                        ...u,
                        expiresAt: newExpiryDate.toISOString(),
                        accessToken: newAccessToken,
                        accessUrl: newAccessUrl,
                        isActive: true
                    }
                    : u
            ));

            addAuditLog('GUEST_USER_REACTIVATED', user.id, user.guestEmail);

            // Resend invitation
            sendAccessNotification(
                user.guestEmail,
                user.guestName,
                user.guestCompany,
                user.assetName,
                newAccessUrl,
                expiryDays,
                newAccessToken
            );

            alert(`✓ Guest access reactivated for ${user.guestName}! A new invitation has been sent.`);
        }
    };

    const validateForm = (): boolean => {
        const errors = {
            email: '',
            name: '',
            company: ''
        };
        let isValid = true;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!guestInfo.email) {
            errors.email = 'Email is required';
            isValid = false;
        } else if (!emailRegex.test(guestInfo.email)) {
            errors.email = 'Please enter a valid email address';
            isValid = false;
        }

        if (!guestInfo.name) {
            errors.name = 'Name is required';
            isValid = false;
        } else if (guestInfo.name.length < 2) {
            errors.name = 'Name must be at least 2 characters';
            isValid = false;
        }

        if (!guestInfo.company) {
            errors.company = 'Company name is required';
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    const generateAccessToken = () => {
        return Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15) +
            Date.now().toString(36);
    };

    const createGuestUser = () => {
        if (selectedAsset.length === 0) {
            alert('Please select at least one asset to share');
            return;
        }

        if (userSelectionType === 'new' && !validateForm()) {
            return;
        }

        // Create guest users for each selected asset
        const newUsers: GuestUser[] = selectedAsset.map((asset, index) => {
            const accessToken = generateAccessToken();
            const expiresAt = new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000);
            const guestAccessUrl = `${window.location.origin}/guest/access/${accessToken}`;

            return {
                id: `${Date.now()}_${index}`,
                assetId: asset.id,
                assetName: asset.name,
                assetType: asset.type as any,
                accessToken,
                accessUrl: guestAccessUrl,
                createdAt: new Date().toISOString(),
                expiresAt: expiresAt.toISOString(),
                accessCount: 0,
                isActive: true,
                createdBy: 'current-user@example.com',
                guestEmail: guestInfo.email,
                guestName: guestInfo.name,
                guestCompany: guestInfo.company
            };
        });

        setGuestUsers([...newUsers, ...guestUsers]);

        // Create assignment record
        const newAssignment: GuestAssignment = {
            guestId: guestInfo.email,
            assetIds: selectedAsset.map(a => a.id),
            assignedAt: new Date().toISOString()
        };
        setAssignments([...assignments, newAssignment]);

        // Send notification email with all assets
        sendMultiAssetNotification(
            guestInfo.email,
            guestInfo.name,
            guestInfo.company,
            selectedAsset,
            newUsers.map(u => u.accessUrl),
            expiryDays
        );

        // Add audit log
        addAuditLog('GUEST_USER_CREATED_MULTI', guestInfo.email, `${selectedAsset.length} assets assigned`);

        // If this is a new user, ask about saving for future
        if (userSelectionType === 'new') {
            setTempCreatedGuest({
                email: guestInfo.email,
                name: guestInfo.name,
                company: guestInfo.company
            });
            setShowSavePrompt(true);
        } else {
            // For existing user, just close and show success
            alert(`✓ Guest access granted successfully!\n\nAn invitation has been sent to ${guestInfo.email}\n\nAccess links copied to clipboard!`);
            navigator.clipboard.writeText(newUsers.map(u => u.accessUrl).join('\n'));
            resetAndCloseModal();
        }
    };

    const handleSaveForFuture = (shouldSave: boolean) => {
        if (shouldSave && tempCreatedGuest) {
            saveContact(
                tempCreatedGuest.email,
                tempCreatedGuest.name,
                tempCreatedGuest.company,
                selectedAsset.map(a => a.id)
            );
            alert(`✓ ${tempCreatedGuest.name} has been saved to your contacts for future use!`);
        }

        alert(`✓ Guest access granted successfully!\n\nAn invitation has been sent to ${tempCreatedGuest?.email}\n\nAccess links copied to clipboard!`);
        navigator.clipboard.writeText(selectedAsset.map(a =>
            `${window.location.origin}/guest/access/${generateAccessToken()}`
        ).join('\n'));

        resetAndCloseModal();
        setShowSavePrompt(false);
        setTempCreatedGuest(null);
    };

    const sendMultiAssetNotification = (email: string, name: string, company: string, assets: any[], accessUrls: string[], expiryDays: number) => {
        const assetsList = assets.map((asset, index) => `
            <div style="background-color: #f9fafb; padding: 10px; margin-bottom: 10px; border-radius: 6px;">
                <strong>${asset.name}</strong> (${asset.type})<br/>
                <a href="${accessUrls[index]}">${accessUrls[index]}</a>
            </div>
        `).join('');

        const emailContent = {
            to: email,
            subject: `Guest Access to ${assets.length} Asset(s) - SIMB Platform`,
            html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                    <div style="background-color: #3B82F6; padding: 20px; text-align: center;">
                        <h1 style="color: white; margin: 0;">SIMB Platform</h1>
                    </div>
                    <div style="padding: 20px; border: 1px solid #e5e7eb; border-top: none;">
                        <h2>Dear ${name},</h2>
                        <p>You have been granted guest access to <strong>${assets.length} asset(s)</strong> from ${company}.</p>
                        
                        <div style="background-color: #f3f4f6; padding: 15px; border-radius: 8px; margin: 20px 0;">
                            <h3 style="margin-top: 0;">Access Information</h3>
                            ${assetsList}
                            <p><strong>Access Duration:</strong> ${expiryDays} days</p>
                            <p><strong>Expires On:</strong> ${new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                        </div>
                        
                        <div style="background-color: #fef3c7; padding: 15px; border-radius: 8px; margin: 20px 0;">
                            <h3 style="margin-top: 0; color: #92400e;">Important Notes</h3>
                            <ul style="margin: 0; padding-left: 20px;">
                                <li>Each link provides access ONLY to the specific asset mentioned above</li>
                                <li>You will not have access to any other system features or content</li>
                                <li>Your access will expire automatically after ${expiryDays} days</li>
                                <li>All access attempts are tracked and logged for security</li>
                                <li>Do not share these links with anyone else</li>
                            </ul>
                        </div>
                        
                        <p>For security reasons, these links are unique to you and your access will be monitored.</p>
                        <p>If you have any questions, please contact the person who shared this content with you.</p>
                        
                        <hr style="margin: 20px 0; border-color: #e5e7eb;" />
                        <p style="color: #6b7280; font-size: 12px;">This is an automated message, please do not reply.</p>
                    </div>
                </div>
            `
        };
        console.log('Email sent:', emailContent);
    };

    const sendAccessNotification = (email: string, name: string, company: string, assetName: string, accessUrl: string, expiryDays: number, accessToken: string) => {
        const emailContent = {
            to: email,
            subject: `Guest Access to "${assetName}" - SIMB Platform`,
            html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                    <div style="background-color: #3B82F6; padding: 20px; text-align: center;">
                        <h1 style="color: white; margin: 0;">SIMB Platform</h1>
                    </div>
                    <div style="padding: 20px; border: 1px solid #e5e7eb; border-top: none;">
                        <h2>Dear ${name},</h2>
                        <p>You have been granted guest access to <strong>"${assetName}"</strong> from ${company}.</p>
                        
                        <div style="background-color: #f3f4f6; padding: 15px; border-radius: 8px; margin: 20px 0;">
                            <h3 style="margin-top: 0;">Access Information</h3>
                            <p><strong>Asset:</strong> ${assetName}</p>
                            <p><strong>Access Link:</strong> <a href="${accessUrl}">${accessUrl}</a></p>
                            <p><strong>Access Duration:</strong> ${expiryDays} days</p>
                            <p><strong>Expires On:</strong> ${new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                        </div>
                        
                        <div style="background-color: #fef3c7; padding: 15px; border-radius: 8px; margin: 20px 0;">
                            <h3 style="margin-top: 0; color: #92400e;">Important Notes</h3>
                            <ul style="margin: 0; padding-left: 20px;">
                                <li>This link provides access ONLY to the specific asset mentioned above</li>
                                <li>You will not have access to any other system features or content</li>
                                <li>Your access will expire automatically after ${expiryDays} days</li>
                                <li>All access attempts are tracked and logged for security</li>
                                <li>Do not share this link with anyone else</li>
                            </ul>
                        </div>
                        
                        <p>For security reasons, this link is unique to you and your access will be monitored.</p>
                        <p>If you have any questions, please contact the person who shared this content with you.</p>
                        
                        <hr style="margin: 20px 0; border-color: #e5e7eb;" />
                        <p style="color: #6b7280; font-size: 12px;">This is an automated message, please do not reply.</p>
                    </div>
                </div>
            `
        };
        console.log('Email sent:', emailContent);
    };

    const addAuditLog = (action: string, userId: string, details: string) => {
        const logEntry = {
            action,
            userId,
            details,
            timestamp: new Date().toISOString(),
            tenantId
        };
        console.log('Audit Log:', logEntry);
    };

    const toggleUserStatus = (userId: string, currentStatus: boolean) => {
        const user = guestUsers.find(u => u.id === userId);
        const newStatus = !currentStatus;

        if (window.confirm(`Are you sure you want to ${newStatus ? 'activate' : 'deactivate'} access for ${user?.guestName}?`)) {
            setGuestUsers(guestUsers.map(user =>
                user.id === userId ? { ...user, isActive: newStatus } : user
            ));
            addAuditLog(newStatus ? 'GUEST_USER_ACTIVATED' : 'GUEST_USER_DEACTIVATED', userId, user?.guestEmail || '');
            alert(`✓ Guest user access ${newStatus ? 'activated' : 'deactivated'} successfully!`);
        }
    };

    const handleSelectUser = (userId: string) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    const handleSelectAll = () => {
        const filteredUsers = getFilteredUsers();
        const activeUsers = filteredUsers.filter(user => !isExpired(user.expiresAt)).map(user => user.id);
        if (selectedUsers.length === activeUsers.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(activeUsers);
        }
    };

    const bulkActivate = () => {
        if (selectedUsers.length === 0) {
            alert('Please select at least one guest user');
            return;
        }

        if (window.confirm(`Are you sure you want to activate ${selectedUsers.length} guest user(s)?`)) {
            setGuestUsers(guestUsers.map(user =>
                selectedUsers.includes(user.id) ? { ...user, isActive: true } : user
            ));
            selectedUsers.forEach(userId => {
                const user = guestUsers.find(u => u.id === userId);
                addAuditLog('BULK_ACTIVATE', userId, user?.guestEmail || '');
            });
            alert(`✓ ${selectedUsers.length} guest user(s) activated successfully!`);
            setSelectedUsers([]);
            setShowBulkActions(false);
        }
    };

    const bulkDeactivate = () => {
        if (selectedUsers.length === 0) {
            alert('Please select at least one guest user');
            return;
        }

        if (window.confirm(`Are you sure you want to deactivate ${selectedUsers.length} guest user(s)?`)) {
            setGuestUsers(guestUsers.map(user =>
                selectedUsers.includes(user.id) ? { ...user, isActive: false } : user
            ));
            selectedUsers.forEach(userId => {
                const user = guestUsers.find(u => u.id === userId);
                addAuditLog('BULK_DEACTIVATE', userId, user?.guestEmail || '');
            });
            alert(`✓ ${selectedUsers.length} guest user(s) deactivated successfully!`);
            setSelectedUsers([]);
            setShowBulkActions(false);
        }
    };

    const exportAuditLogs = () => {
        let logsToExport = accessLogs;

        if (exportDateRange.start && exportDateRange.end) {
            logsToExport = accessLogs.filter(log =>
                log.accessedAt >= exportDateRange.start &&
                log.accessedAt <= exportDateRange.end
            );
        }

        const csvData = logsToExport.map(log => ({
            'Date/Time': new Date(log.accessedAt).toLocaleString(),
            'Guest Name': log.guestName,
            'Guest Email': log.guestEmail,
            'Action': log.action,
            'Asset': log.assetAccessed,
            'IP Address': log.ipAddress,
            'Location': log.location || 'N/A',
            'User Agent': log.userAgent,
            'Session ID': log.sessionId || 'N/A'
        }));

        const csvContent = [
            Object.keys(csvData[0] || {}).join(','),
            ...csvData.map(row => Object.values(row).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        setShowExportModal(false);
        alert('✓ Audit logs exported successfully!');
    };

    const resetAndCloseModal = () => {
        setShowCreateModal(false);
        setUserSelectionType(null);
        setSelectedExistingUser(null);
        setGuestInfo({ email: '', name: '', company: '' });
        setFormErrors({ email: '', name: '', company: '' });
        setSelectedAsset([]);
        setExpiryDays(7);
        setSelectedContactId(null);
        setIsMultiAssetMode(false);
        setSaveForFuture(true);
    };

    const resetForm = () => {
        setGuestInfo({ email: '', name: '', company: '' });
        setFormErrors({ email: '', name: '', company: '' });
        setSelectedAsset([]);
        setExpiryDays(7);
        setSelectedContactId(null);
        setIsMultiAssetMode(false);
        setSelectedExistingUser(null);
    };

    const isExpired = (expiresAt: string) => {
        return new Date(expiresAt) < new Date();
    };

    const getExpiryStatus = (expiresAt: string) => {
        const expiry = new Date(expiresAt);
        const now = new Date();
        const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysLeft < 0) return { text: 'Expired', color: 'text-red-600', bg: 'bg-red-50', days: 0 };
        if (daysLeft <= 3) return { text: `Expires in ${daysLeft} days`, color: 'text-orange-600', bg: 'bg-orange-50', days: daysLeft };
        return { text: `${daysLeft} days remaining`, color: 'text-green-600', bg: 'bg-green-50', days: daysLeft };
    };

    const getAssetIcon = (type: string) => {
        switch (type) {
            case 'campaign': return '📢';
            case 'document': return '📄';
            case 'report': return '📊';
            case 'dashboard': return '📈';
            default: return '🔗';
        }
    };

    const copyLinkToClipboard = (url: string) => {
        navigator.clipboard.writeText(url);
        alert('Access link copied to clipboard!');
    };

    const resendInvitation = (user: GuestUser) => {
        const daysLeft = Math.ceil((new Date(user.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        sendAccessNotification(
            user.guestEmail,
            user.guestName,
            user.guestCompany,
            user.assetName,
            user.accessUrl,
            daysLeft > 0 ? daysLeft : 7,
            user.accessToken
        );
        addAuditLog('GUEST_INVITATION_RESENT', user.id, user.guestEmail);
        alert(`✓ Invitation resent to ${user.guestEmail}`);
    };

    const getFilteredUsers = () => {
        let filtered = guestUsers;

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(user =>
                user.guestName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.guestEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.guestCompany.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.assetName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Status filter
        if (filterStatus === 'active') {
            filtered = filtered.filter(user => user.isActive && !isExpired(user.expiresAt));
        } else if (filterStatus === 'expired') {
            filtered = filtered.filter(user => isExpired(user.expiresAt));
        } else if (filterStatus === 'deactivated') {
            filtered = filtered.filter(user => !user.isActive && !isExpired(user.expiresAt));
        }

        // Asset filter
        if (filterAsset !== 'all') {
            filtered = filtered.filter(user => user.assetId === filterAsset);
        }

        return filtered;
    };

    const getActiveUsersCount = () => {
        return guestUsers.filter(user => user.isActive && !isExpired(user.expiresAt)).length;
    };

    const getDeactivatedUsersCount = () => {
        return guestUsers.filter(user => !user.isActive && !isExpired(user.expiresAt)).length;
    };

    const getExpiredUsersCount = () => {
        return guestUsers.filter(user => isExpired(user.expiresAt)).length;
    };

    const toggleFavoriteContact = (contactId: string) => {
        setSavedContacts(savedContacts.map(contact =>
            contact.id === contactId
                ? { ...contact, isFavorite: !contact.isFavorite }
                : contact
        ));
    };

    const filteredUsers = getFilteredUsers();

    return (
        <div className="space-y-6">
            {/* Header Info Banner */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start gap-4">
                    <div className="text-4xl">👥</div>
                    <div className="flex-1">
                        <h3 className="font-semibold text-blue-900 text-lg">Guest User Management</h3>
                        <p className="text-sm text-blue-700 mt-1">
                            Create temporary guest user accounts for external users to view specific campaigns or assets.
                            Guest users will ONLY have access to the shared item, not the entire system.
                            All access is tracked and logged for security compliance.
                        </p>
                        <div className="flex gap-4 mt-3">
                            <div className="flex items-center gap-1 text-xs text-blue-600">
                                <span>✓</span> Limited Access Only
                            </div>
                            <div className="flex items-center gap-1 text-xs text-blue-600">
                                <span>✓</span> Configurable Expiry
                            </div>
                            <div className="flex items-center gap-1 text-xs text-blue-600">
                                <span>✓</span> Full Audit Trail
                            </div>
                            <div className="flex items-center gap-1 text-xs text-blue-600">
                                <span>✓</span> Saved Contacts
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => window.open('/guest/dashboard', '_blank', 'noopener,noreferrer')}
                        className="shrink-0 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        title="Open the OTP-protected asset access flow in a new tab"
                    >
                        Test OTP Access
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-2xl mb-2">👥</div>
                    <div className="text-2xl font-bold text-gray-800">{guestUsers.length}</div>
                    <div className="text-sm text-gray-500">Total Guest Users</div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-2xl mb-2">✅</div>
                    <div className="text-2xl font-bold text-green-600">{getActiveUsersCount()}</div>
                    <div className="text-sm text-gray-500">Active Users</div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-2xl mb-2">⛔</div>
                    <div className="text-2xl font-bold text-red-600">{getDeactivatedUsersCount()}</div>
                    <div className="text-sm text-gray-500">Deactivated</div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-2xl mb-2">⏰</div>
                    <div className="text-2xl font-bold text-orange-600">{getExpiredUsersCount()}</div>
                    <div className="text-sm text-gray-500">Expired</div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-2xl mb-2">👁️</div>
                    <div className="text-2xl font-bold text-blue-600">{accessLogs.length}</div>
                    <div className="text-sm text-gray-500">Total Views</div>
                </div>
            </div>

            {/* Search and Filters Bar */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                        <input
                            type="text"
                            placeholder="Search by name, email, company..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value as any)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Users</option>
                            <option value="active">Active Only</option>
                            <option value="expired">Expired Only</option>
                            <option value="deactivated">Deactivated Only</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Asset Type</label>
                        <select
                            value={filterAsset}
                            onChange={(e) => setFilterAsset(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Assets</option>
                            <option value="camp-1">Summer Sale Campaign</option>
                            <option value="camp-2">Product Launch Campaign</option>
                            <option value="camp-3">Email Marketing Campaign</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setSearchTerm('');
                                setFilterStatus('all');
                                setFilterAsset('all');
                            }}
                            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Actions Bar */}
            <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="flex gap-3">
                    <button
                        onClick={() => setShowAuditLogs(!showAuditLogs)}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition flex items-center gap-2"
                    >
                        📋 {showAuditLogs ? 'Hide' : 'Show'} Audit Logs
                    </button>
                    <button
                        onClick={() => setShowExportModal(true)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                    >
                        📥 Export Audit Logs
                    </button>
                    {selectedUsers.length > 0 && (
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowBulkActions(!showBulkActions)}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
                            >
                                📌 Bulk Actions ({selectedUsers.length})
                            </button>
                            {showBulkActions && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={bulkActivate}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                                    >
                                        Activate All
                                    </button>
                                    <button
                                        onClick={bulkDeactivate}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                                    >
                                        Deactivate All
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <button
                    onClick={() => {
                        resetForm();
                        setShowCreateModal(true);
                    }}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 shadow-sm"
                >
                    <span className="text-xl">+</span> Create Guest User
                </button>
            </div>

            {/* Saved Contacts Quick Access */}
            {savedContacts.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <div className="flex justify-between items-center">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-800">⭐ Saved Contacts</h2>
                                <p className="text-sm text-gray-500 mt-1">Quickly reuse previously invited guests</p>
                            </div>
                            <button
                                onClick={() => setShowSavedContacts(!showSavedContacts)}
                                className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                                {showSavedContacts ? 'Show Less' : 'Show All'}
                            </button>
                        </div>
                    </div>
                    {showSavedContacts && (
                        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {savedContacts
                                .sort((a, b) => (b.isFavorite ? 1 : 0) - (a.isFavorite ? 1 : 0))
                                .map(contact => (
                                    <div key={contact.id} className="p-3 border border-gray-200 rounded-lg hover:shadow-md transition">
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex-1">
                                                <div className="font-medium text-gray-900">{contact.name}</div>
                                                <div className="text-xs text-gray-500">{contact.email}</div>
                                                <div className="text-xs text-gray-400">{contact.company}</div>
                                            </div>
                                            <button
                                                onClick={() => toggleFavoriteContact(contact.id)}
                                                className="text-xl"
                                            >
                                                {contact.isFavorite ? '⭐' : '☆'}
                                            </button>
                                        </div>
                                        <div className="flex gap-2 mt-2">
                                            <button
                                                onClick={() => {
                                                    setUserSelectionType('existing');
                                                    loadContactDetails(contact);
                                                    setShowCreateModal(true);
                                                }}
                                                className="flex-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                                            >
                                                Use Contact
                                            </button>
                                            {contact.recentAssets && contact.recentAssets.length > 0 && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                    Previously used: {contact.recentAssets.length} asset(s)
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            )}

            {/* Guest Users Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-800">Guest Users</h2>
                            <p className="text-sm text-gray-500 mt-1">Manage and monitor all guest users and their access</p>
                        </div>
                        {filteredUsers.filter(user => !isExpired(user.expiresAt)).length > 0 && (
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selectedUsers.length === filteredUsers.filter(user => !isExpired(user.expiresAt)).length && filteredUsers.filter(user => !isExpired(user.expiresAt)).length > 0}
                                    onChange={handleSelectAll}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                />
                                <span className="text-sm text-gray-600">Select All</span>
                            </label>
                        )}
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600 w-12">
                                    <span className="sr-only">Select</span>
                                </th>
                                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Guest User</th>
                                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Shared Asset</th>
                                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Expiry</th>
                                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Views</th>
                                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Status</th>
                                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map(user => {
                                const expiryStatus = getExpiryStatus(user.expiresAt);
                                const expired = isExpired(user.expiresAt);
                                const isActive = user.isActive && !expired;
                                const canToggle = !expired;

                                return (
                                    <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                                        <td className="px-6 py-4">
                                            {!expired && (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(user.id)}
                                                    onChange={() => handleSelectUser(user.id)}
                                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                />
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">{user.guestName}</div>
                                            <div className="text-xs text-gray-500">{user.guestEmail}</div>
                                            <div className="text-xs text-gray-400">{user.guestCompany}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <span className="text-2xl">{getAssetIcon(user.assetType)}</span>
                                                <div>
                                                    <div className="font-medium text-gray-900">{user.assetName}</div>
                                                    <div className="text-xs text-gray-500 capitalize">{user.assetType}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className={`${expiryStatus.color} font-medium`}>
                                                {expiryStatus.text}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {new Date(user.expiresAt).toLocaleDateString()}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium">{user.accessCount} views</div>
                                            <div className="text-xs text-gray-400">total accesses</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {expired ? (
                                                <div className="flex flex-col gap-1">
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                                                        Expired
                                                    </span>
                                                    <button
                                                        onClick={() => reactivateExpiredGuest(user)}
                                                        className="text-xs text-blue-600 hover:text-blue-800"
                                                    >
                                                        Reactivate
                                                    </button>
                                                </div>
                                            ) : (
                                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={user.isActive}
                                                        onChange={() => toggleUserStatus(user.id, user.isActive)}
                                                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                        disabled={!canToggle}
                                                    />
                                                    <span className={`text-sm ${user.isActive ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}`}>
                                                        {user.isActive ? 'Active' : 'Deactivated'}
                                                    </span>
                                                </label>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex gap-2 flex-wrap">
                                                <button
                                                    onClick={() => viewUserAuditLogs(user)}
                                                    className="text-purple-600 hover:text-purple-800 text-sm px-2 py-1 hover:bg-purple-50 rounded flex items-center gap-1"
                                                    title="View Audit Logs"
                                                >
                                                    📋 Audit Logs
                                                </button>
                                                {!expired && user.isActive && (
                                                    <button
                                                        onClick={() => resendInvitation(user)}
                                                        className="text-green-600 hover:text-green-800 text-sm px-2 py-1 hover:bg-green-50 rounded"
                                                    >
                                                        Resend
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => copyLinkToClipboard(user.accessUrl)}
                                                    className="text-blue-600 hover:text-blue-800 text-sm px-2 py-1 hover:bg-blue-50 rounded"
                                                >
                                                    Copy Link
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredUsers.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                                        <div className="text-6xl mb-3">👥</div>
                                        <p>No guest users found</p>
                                        <p className="text-sm mt-1">Click "Create Guest User" to get started</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Audit Logs Section */}
            {showAuditLogs && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-800">Access Tracking & Audit Logs</h2>
                        <p className="text-sm text-gray-500 mt-1">Showing audit activity from the database</p>
                    </div>
                    <div className="p-6">
                        <div className="mb-5 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                            <div>
                            <label htmlFor="access-audit-tenant-filter" className="block text-sm font-medium text-gray-700 mb-1">
                                Organization
                            </label>
                            <select
                                id="access-audit-tenant-filter"
                                value={selectedAuditTenantId}
                                onChange={(event) => {
                                    const auditTenantId = event.target.value;
                                    setSelectedAuditTenantId(auditTenantId);
                                    setSelectedAuditAction('');
                                    loadAuditActions(auditTenantId);
                                    loadAccessLogs(auditTenantId, '');
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All organizations</option>
                                {auditTenants.map((auditTenant) => (
                                    <option key={auditTenant.id} value={auditTenant.id}>{auditTenant.name}</option>
                                ))}
                            </select>
                            </div>
                            <div>
                                <label htmlFor="access-audit-action-filter" className="block text-sm font-medium text-gray-700 mb-1">
                                    Action
                                </label>
                                <select
                                    id="access-audit-action-filter"
                                    value={selectedAuditAction}
                                    onChange={(event) => {
                                        const action = event.target.value;
                                        setSelectedAuditAction(action);
                                        loadAccessLogs(selectedAuditTenantId, action);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All actions</option>
                                    {availableAuditActions.map((action) => (
                                        <option key={action} value={action}>{formatAuditAction(action)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {accessLogsLoading ? (
                                <div className="text-center text-gray-500 py-8">Loading audit logs...</div>
                            ) : accessLogsError ? (
                                <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                                    {accessLogsError}
                                </div>
                            ) : accessLogs.map(log => (
                                <div key={log.id} className={`p-4 rounded-lg border ${log.action === 'login' ? 'bg-green-50 border-green-200' : log.action === 'logout' ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'} hover:shadow-sm transition`}>
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`text-lg ${log.action === 'login' ? '🟢' : log.action === 'logout' ? '🟡' : '🔵'}`}>
                                                    {log.action === 'login' ? '🔓' : log.action === 'logout' ? '🔒' : '👁️'}
                                                </span>
                                                <span className="font-semibold text-gray-900">{log.assetAccessed}</span>
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${log.action === 'login' ? 'bg-green-100 text-green-700' : log.action === 'logout' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                                                    {log.action.toUpperCase()}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-700">
                                                👤 {log.guestName} ({log.guestEmail})
                                            </p>
                                            <div className="flex gap-3 mt-2 text-xs text-gray-500">
                                                <span>🌐 IP: {log.ipAddress}</span>
                                                <span>📍 Location: {log.location || 'N/A'}</span>
                                                <span>🖥️ Browser: {log.userAgent}</span>
                                                {log.sessionId && <span>🔑 Session: {log.sessionId}</span>}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-gray-500">
                                                {new Date(log.accessedAt).toLocaleDateString()}
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                {new Date(log.accessedAt).toLocaleTimeString()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {!accessLogsLoading && !accessLogsError && accessLogs.length === 0 && (
                                <div className="text-center text-gray-500 py-8">
                                    <div className="text-5xl mb-3">📊</div>
                                    <p>No audit logs found</p>
                                    <p className="text-sm mt-1">Audit logs from the database will appear here</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* User Audit Log Modal */}
            {showUserAuditModal && selectedUserForAudit && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-800">Audit Logs: {selectedUserForAudit.guestName}</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {selectedUserForAudit.guestEmail} • {selectedUserForAudit.guestCompany}
                                </p>
                            </div>
                            <button
                                onClick={() => setShowUserAuditModal(false)}
                                className="text-gray-400 hover:text-gray-600 text-2xl"
                            >
                                ×
                            </button>
                        </div>

                        <div className="p-6 flex-1 overflow-y-auto">
                            {/* User Info Summary */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                <div className="bg-blue-50 rounded-lg p-3">
                                    <div className="text-xs text-blue-600 mb-1">Total Access Events</div>
                                    <div className="text-2xl font-bold text-blue-900">{userAuditLogs.length}</div>
                                </div>
                                <div className="bg-green-50 rounded-lg p-3">
                                    <div className="text-xs text-green-600 mb-1">Logins</div>
                                    <div className="text-2xl font-bold text-green-900">
                                        {userAuditLogs.filter(log => log.action === 'login').length}
                                    </div>
                                </div>
                                <div className="bg-yellow-50 rounded-lg p-3">
                                    <div className="text-xs text-yellow-600 mb-1">Logouts</div>
                                    <div className="text-2xl font-bold text-yellow-900">
                                        {userAuditLogs.filter(log => log.action === 'logout').length}
                                    </div>
                                </div>
                                <div className="bg-purple-50 rounded-lg p-3">
                                    <div className="text-xs text-purple-600 mb-1">Content Views</div>
                                    <div className="text-2xl font-bold text-purple-900">
                                        {userAuditLogs.filter(log => log.action === 'view_access').length}
                                    </div>
                                </div>
                            </div>

                            {/* Export Controls */}
                            <div className="mb-4 flex gap-4 items-end">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                    <input
                                        type="date"
                                        value={userExportDateRange.start}
                                        onChange={(e) => setUserExportDateRange({ ...userExportDateRange, start: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                    <input
                                        type="date"
                                        value={userExportDateRange.end}
                                        onChange={(e) => setUserExportDateRange({ ...userExportDateRange, end: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                                <div>
                                    <button
                                        onClick={exportUserAuditLogs}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                                    >
                                        📥 Export Logs
                                    </button>
                                </div>
                                <div>
                                    <button
                                        onClick={() => setUserExportDateRange({ start: '', end: '' })}
                                        className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                                    >
                                        Clear Filters
                                    </button>
                                </div>
                            </div>

                            {/* Audit Logs List */}
                            <div className="space-y-3">
                                {userAuditLogs
                                    .filter(log => {
                                        if (userExportDateRange.start && userExportDateRange.end) {
                                            return log.accessedAt >= userExportDateRange.start &&
                                                log.accessedAt <= userExportDateRange.end;
                                        }
                                        return true;
                                    })
                                    .map(log => (
                                        <div key={log.id} className={`p-4 rounded-lg border ${log.action === 'login' ? 'bg-green-50 border-green-200' : log.action === 'logout' ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'} hover:shadow-sm transition`}>
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className={`text-lg`}>
                                                            {log.action === 'login' ? '🔓' : log.action === 'logout' ? '🔒' : '👁️'}
                                                        </span>
                                                        <span className="font-semibold text-gray-900">{log.assetAccessed}</span>
                                                        <span className={`text-xs px-2 py-0.5 rounded-full ${log.action === 'login' ? 'bg-green-100 text-green-700' : log.action === 'logout' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                                                            {log.action.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div className="flex gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                                                        <span>🌐 IP: {log.ipAddress}</span>
                                                        <span>📍 Location: {log.location || 'N/A'}</span>
                                                        <span>🖥️ Browser: {log.userAgent}</span>
                                                        {log.sessionId && <span>🔑 Session: {log.sessionId}</span>}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-medium text-gray-700">
                                                        {new Date(log.accessedAt).toLocaleDateString()}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {new Date(log.accessedAt).toLocaleTimeString()}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                {userAuditLogs.filter(log => {
                                    if (userExportDateRange.start && userExportDateRange.end) {
                                        return log.accessedAt >= userExportDateRange.start &&
                                            log.accessedAt <= userExportDateRange.end;
                                    }
                                    return true;
                                }).length === 0 && (
                                        <div className="text-center text-gray-500 py-8">
                                            <div className="text-5xl mb-3">📊</div>
                                            <p>No audit logs found for this period</p>
                                        </div>
                                    )}
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                            <button
                                onClick={() => setShowUserAuditModal(false)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Export Modal */}
            {showExportModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl w-full max-w-md">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-800">Export Audit Logs</h3>
                        </div>
                        <div className="p-6">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                <input
                                    type="date"
                                    value={exportDateRange.start}
                                    onChange={(e) => setExportDateRange({ ...exportDateRange, start: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                <input
                                    type="date"
                                    value={exportDateRange.end}
                                    onChange={(e) => setExportDateRange({ ...exportDateRange, end: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
                            <button
                                onClick={() => setShowExportModal(false)}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={exportAuditLogs}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                Export CSV
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Guest User Modal */}
            {showCreateModal && !showSavePrompt && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-xl font-semibold text-gray-800">Create Guest User</h3>
                                <button
                                    onClick={() => resetAndCloseModal()}
                                    className="text-gray-400 hover:text-gray-600 text-2xl"
                                >
                                    ×
                                </button>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">Choose whether to use an existing contact or create a new one</p>
                        </div>

                        <div className="p-6">
                            {/* User Selection Step - Show if no type selected yet */}
                            {!userSelectionType && (
                                <div className="space-y-4">
                                    <div className="text-center mb-6">
                                        <div className="text-6xl mb-3">👤</div>
                                        <p className="text-gray-600">How would you like to proceed?</p>
                                    </div>

                                    <button
                                        onClick={() => setUserSelectionType('existing')}
                                        className="w-full p-6 border-2 border-blue-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition text-left"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="text-4xl">📇</div>
                                            <div>
                                                <div className="font-semibold text-lg">Use Existing User</div>
                                                <div className="text-sm text-gray-500">Select from your saved contacts</div>
                                            </div>
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => setUserSelectionType('new')}
                                        className="w-full p-6 border-2 border-green-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition text-left"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="text-4xl">✨</div>
                                            <div>
                                                <div className="font-semibold text-lg">Create New User</div>
                                                <div className="text-sm text-gray-500">Enter guest details manually</div>
                                            </div>
                                        </div>
                                    </button>
                                </div>
                            )}

                            {/* Existing User Selection */}
                            {userSelectionType === 'existing' && (
                                <div className="space-y-4">
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Select Existing User <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            value={selectedExistingUser?.id || ''}
                                            onChange={(e) => {
                                                const contact = savedContacts.find(c => c.id === e.target.value);
                                                if (contact) loadContactDetails(contact);
                                            }}
                                        >
                                            <option value="">-- Select a saved contact --</option>
                                            {savedContacts.map(contact => (
                                                <option key={contact.id} value={contact.id}>
                                                    {contact.isFavorite ? '⭐ ' : '  '}{contact.name} - {contact.email} ({contact.company})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {selectedExistingUser && (
                                        <>
                                            <div className="p-4 bg-gray-50 rounded-lg">
                                                <div className="text-sm font-medium text-gray-700 mb-2">Selected Contact Details:</div>
                                                <div className="space-y-1 text-sm">
                                                    <p><strong>Name:</strong> {selectedExistingUser.name}</p>
                                                    <p><strong>Email:</strong> {selectedExistingUser.email}</p>
                                                    <p><strong>Company:</strong> {selectedExistingUser.company}</p>
                                                    <p><strong>Previously used:</strong> {selectedExistingUser.usageCount} times</p>
                                                    <p><strong>Last used:</strong> {new Date(selectedExistingUser.lastUsed).toLocaleDateString()}</p>
                                                </div>
                                            </div>

                                            {/* Asset Selection for Existing User */}
                                            <div className="mt-4">
                                                <h4 className="text-md font-semibold text-gray-700 mb-3 pb-2 border-b">Content to Share</h4>

                                                <div className="mb-4">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Select Asset(s) <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-300 rounded-lg p-3">
                                                        {availableAssets.map(asset => (
                                                            <label key={asset.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedAsset.some(a => a.id === asset.id)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedAsset([...selectedAsset, asset]);
                                                                        } else {
                                                                            setSelectedAsset(selectedAsset.filter(a => a.id !== asset.id));
                                                                        }
                                                                    }}
                                                                    className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                                                />
                                                                <span className="text-2xl">{getAssetIcon(asset.type)}</span>
                                                                <div>
                                                                    <div className="font-medium">{asset.name}</div>
                                                                    <div className="text-xs text-gray-500 capitalize">{asset.type}</div>
                                                                </div>
                                                            </label>
                                                        ))}
                                                    </div>
                                                    {selectedAsset.length > 0 && (
                                                        <div className="mt-2 text-sm text-blue-600">
                                                            {selectedAsset.length} asset(s) selected
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* New User Form */}
                            {userSelectionType === 'new' && (
                                <div className="space-y-4">
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Full Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={guestInfo.name}
                                            onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value })}
                                            placeholder="Enter guest's full name"
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${formErrors.name ? 'border-red-500' : 'border-gray-300'}`}
                                        />
                                        {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email Address <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            value={guestInfo.email}
                                            onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })}
                                            placeholder="guest@company.com"
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                                        />
                                        {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Company Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={guestInfo.company}
                                            onChange={(e) => setGuestInfo({ ...guestInfo, company: e.target.value })}
                                            placeholder="Enter guest's company name"
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${formErrors.company ? 'border-red-500' : 'border-gray-300'}`}
                                        />
                                        {formErrors.company && <p className="text-xs text-red-500 mt-1">{formErrors.company}</p>}
                                    </div>

                                    {/* Asset Selection for New User */}
                                    <div className="mt-4">
                                        <h4 className="text-md font-semibold text-gray-700 mb-3 pb-2 border-b">Content to Share</h4>

                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Select Asset(s) <span className="text-red-500">*</span>
                                            </label>
                                            <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-300 rounded-lg p-3">
                                                {availableAssets.map(asset => (
                                                    <label key={asset.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedAsset.some(a => a.id === asset.id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedAsset([...selectedAsset, asset]);
                                                                } else {
                                                                    setSelectedAsset(selectedAsset.filter(a => a.id !== asset.id));
                                                                }
                                                            }}
                                                            className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                                        />
                                                        <span className="text-2xl">{getAssetIcon(asset.type)}</span>
                                                        <div>
                                                            <div className="font-medium">{asset.name}</div>
                                                            <div className="text-xs text-gray-500 capitalize">{asset.type}</div>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                            {selectedAsset.length > 0 && (
                                                <div className="mt-2 text-sm text-blue-600">
                                                    {selectedAsset.length} asset(s) selected
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Access Settings - Common for both */}
                            {(userSelectionType === 'existing' && selectedExistingUser) || userSelectionType === 'new' ? (
                                <div className="mt-6">
                                    <h4 className="text-md font-semibold text-gray-700 mb-3 pb-2 border-b">Access Settings</h4>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Access Duration
                                        </label>
                                        <div className="flex gap-2 flex-wrap">
                                            {[1, 3, 7, 14, 30].map(days => (
                                                <button
                                                    key={days}
                                                    type="button"
                                                    onClick={() => setExpiryDays(days)}
                                                    className={`px-4 py-2 rounded-lg transition ${expiryDays === days
                                                        ? 'bg-blue-600 text-white shadow-sm'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        }`}
                                                >
                                                    {days} {days === 1 ? 'day' : 'days'}
                                                </button>
                                            ))}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">Access will expire after {expiryDays} days</p>
                                    </div>

                                    {/* Security Summary */}
                                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                        <p className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                                            <span>🔒</span> Security Summary
                                        </p>
                                        <div className="space-y-1 text-xs text-blue-800">
                                            <p>✓ Guest will ONLY access: <strong>{selectedAsset.length > 0 ? selectedAsset.map(a => a.name).join(', ') : 'Not selected'}</strong></p>
                                            <p>✓ No access to dashboard, settings, campaigns list, or other features</p>
                                            <p>✓ Access expires automatically after {expiryDays} days</p>
                                            <p>✓ All access attempts will be logged and tracked</p>
                                            <p>✓ User can be activated/deactivated at any time</p>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        {/* Modal Footer */}
                        {(userSelectionType === 'existing' && selectedExistingUser) || userSelectionType === 'new' ? (
                            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
                                <button
                                    onClick={() => {
                                        if (userSelectionType === 'existing') {
                                            setUserSelectionType(null);
                                            setSelectedExistingUser(null);
                                            setSelectedAsset([]);
                                        } else {
                                            setUserSelectionType(null);
                                            resetForm();
                                        }
                                    }}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={() => {
                                        if (userSelectionType === 'existing' && !selectedExistingUser) {
                                            alert('Please select an existing user');
                                            return;
                                        }
                                        if (selectedAsset.length === 0) {
                                            alert('Please select at least one asset to share');
                                            return;
                                        }
                                        if (userSelectionType === 'new' && !validateForm()) {
                                            return;
                                        }
                                        createGuestUser();
                                    }}
                                    disabled={selectedAsset.length === 0 || (userSelectionType === 'existing' && !selectedExistingUser)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                                >
                                    <span>👤</span>
                                    Create Guest User
                                </button>
                            </div>
                        ) : userSelectionType ? null : (
                            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
                                <button
                                    onClick={() => resetAndCloseModal()}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Save for Future Prompt Modal */}
            {showSavePrompt && tempCreatedGuest && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl w-full max-w-md">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-800">Save Contact for Future?</h3>
                        </div>
                        <div className="p-6">
                            <div className="text-center mb-4">
                                <div className="text-6xl mb-3">💾</div>
                                <p className="text-gray-700 mb-2">
                                    Would you like to save <strong>{tempCreatedGuest.name}</strong> to your contacts?
                                </p>
                                <p className="text-sm text-gray-500">
                                    This will allow you to quickly grant access to this person again in the future without re-entering their details.
                                </p>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => handleSaveForFuture(false)}
                                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                                >
                                    No, Don't Save
                                </button>
                                <button
                                    onClick={() => handleSaveForFuture(true)}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                                >
                                    Yes, Save Contact
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GuestAccessManager;
